import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { GlobalAssistantEntityType } from './global-ai-assistant-api';
import type { AssistantTaxonomy } from './use-global-ai-assistant-taxonomy';

/**
 * Cache for the `startCustomPageStream` result — the custom-page layout
 * generated by dash-meta's SSE stream.
 *
 * Why cache: the stream takes ~5-10s end-to-end (LLM layout generation +
 * filter resolution). If the user re-invokes "Create custom page for this
 * request" with the same prompt + entity + taxonomy, we can skip the
 * stream entirely and apply the cached layout directly.
 *
 * Cache value: `{ config, filters? }` — the last `config` event (a
 * full `LLMTabContentForDispatch`) and the one `filters` event if the
 * stream emitted one. Matches what the stream runner actually applies.
 *
 * Invalidation strategy: **version-based, not time-based.** Each stored
 * entry carries a `cacheVersion` string. On read, entries with a
 * non-current version are ignored; on write, they're pruned. Bump
 * `CACHE_VERSION` whenever the cached shape could misapply — see the
 * constant's docblock.
 *
 * Tradeoffs:
 * - LLM output is slightly nondeterministic run-to-run. Caching pins one
 *   specific layout until CACHE_VERSION changes. Users get consistent
 *   results on repeated clicks (arguably good) but can't "reroll" until
 *   the next version bump (arguably bad).
 * - Layouts are larger than filter responses (kilobytes per entry).
 *   Smaller cap (10 entries) than the filter cache.
 * - localStorage only — no in-memory tier. Stream invocations are
 *   inherently user-initiated (click), so in-memory de-dup of concurrent
 *   calls isn't a concern the way it is for auto-prefetched filters.
 */

/**
 * Bump this when the cached shape could misapply:
 *
 * 1. `LLMTabContentForDispatch` evolves in a way that
 *    `applyLlmTabContentToTabDispatch` handles differently (new plot
 *    kinds, required fields on PositionedItem, etc.).
 * 2. `TalentDiscoveryAiFilterSearchResponseV2` evolves similarly for the
 *    filters half.
 * 3. dash-meta's page-stream prompt / intent schema changes in a way
 *    that old cached layouts no longer represent what a fresh run would
 *    produce.
 * 4. This module's own stored envelope evolves.
 *
 * Using date-prefixed strings (`2026-04-22-1`) makes bumps auditable in
 * git history.
 */
const CACHE_VERSION = '2026-08-20-1';

const STORAGE_KEY = 'revelio.globalAiAssistant.pageStreamCache.v1';
const MAX_ENTRIES = 10;

export type CachedPageStreamResult = {
  config: LLMTabContentForDispatch;
  filters?: TalentDiscoveryAiFilterSearchResponseV2;
  title?: string;
};

type StoredEntry = {
  key: string;
  result: CachedPageStreamResult;
  cacheVersion: string;
};

function buildKey(
  prompt: string,
  entityType: GlobalAssistantEntityType,
  taxonomy: AssistantTaxonomy,
  activitiesEnabled: boolean
): string {
  // Activity and geography are in the key for the same reason role/skill are:
  // the generated layout's resolved filters are taxonomy-relative, so a
  // metro-resolved layout must never be replayed for an administrative user.
  // `?? 1` — an absent geography taxonomy IS metro (flag off).
  //
  // `activitiesEnabled` is the CAPABILITY, not the taxonomy: it decides whether
  // the layout can hold activity filters at all, so a layout built on an
  // activity-capable page must not be replayed for a page without them.
  // `activitiesKey` only says WHICH activity tree would be used if one ran.
  return `${prompt}\u0001${entityType}\u0001${taxonomy.rolesTax}\u0001${taxonomy.skillsTax}\u0001${taxonomy.activitiesKey}\u0001geo:${taxonomy.geoTax ?? 1}\u0001act:${activitiesEnabled ? 'on' : 'off'}`;
}

function readStore(): StoredEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Structural check + current-version filter — older entries are
    // silently dropped (will be overwritten by fresh runs).
    return parsed.filter(
      (e): e is StoredEntry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as StoredEntry).key === 'string' &&
        typeof (e as StoredEntry).cacheVersion === 'string' &&
        (e as StoredEntry).cacheVersion === CACHE_VERSION &&
        (e as StoredEntry).result !== undefined &&
        typeof (e as StoredEntry).result === 'object'
    );
  } catch {
    return [];
  }
}

function writeStore(entries: StoredEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota errors / private browsing: non-fatal, cache is best-effort.
  }
}

/**
 * Read a cached stream result for this prompt + entity + taxonomy. Returns
 * null on miss or if the entry is from a prior cache version.
 */
export function readCachedPageStream(
  prompt: string,
  entityType: GlobalAssistantEntityType,
  taxonomy: AssistantTaxonomy,
  activitiesEnabled: boolean
): CachedPageStreamResult | null {
  const normalized = prompt.trim();
  if (normalized.length === 0) return null;
  const key = buildKey(normalized, entityType, taxonomy, activitiesEnabled);
  return readStore().find((e) => e.key === key)?.result ?? null;
}

/**
 * Write a stream result to the cache. Called by the bridge after the
 * stream completes successfully. Overwrites an existing entry for the
 * same key; evicts oldest entries (head of the FIFO) when over MAX_ENTRIES.
 */
export function writeCachedPageStream(
  prompt: string,
  entityType: GlobalAssistantEntityType,
  taxonomy: AssistantTaxonomy,
  activitiesEnabled: boolean,
  result: CachedPageStreamResult
): void {
  const normalized = prompt.trim();
  if (normalized.length === 0) return;
  const key = buildKey(normalized, entityType, taxonomy, activitiesEnabled);

  const entries = readStore().filter((e) => e.key !== key);
  entries.push({ key, result, cacheVersion: CACHE_VERSION });
  const overflow = entries.length - MAX_ENTRIES;
  writeStore(overflow > 0 ? entries.slice(overflow) : entries);
}

/** Test hook — clear the cache between tests. */
function __resetPageStreamCacheForTests(): void {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }
}
